import { OFFLINE_STORES, getOfflineStorageDriver, encodeRowForSqlite } from './offlineStorageAdapter.js';
import { createOfflineAttachmentCommand, validateOfflineAttachmentCommand, verifyOfflineAttachmentReceipt, attachmentQueueSnapshot } from './offlineAttachmentCommand.js';
import {projectAcknowledgedAttachment} from './visitaAttachmentProjection.js';

const STORE = OFFLINE_STORES.FILES;
const locks = new Map();
const terminal = new Set(['acknowledged', 'discarded']);
const ownerId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
const copy = value => JSON.parse(JSON.stringify(value));
const hashBytes = async bytes => [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(byte => byte.toString(16).padStart(2, '0')).join('');

export async function offlineFileBytes(data) {
    if (data && typeof data.arrayBuffer === 'function') return new Uint8Array(await data.arrayBuffer());
    if (typeof data !== 'string' || !data) throw Error('No se pudieron leer los bytes del archivo.');
    const encoded = data.startsWith('data:') ? data.slice(data.indexOf(',') + 1) : data;
    try { return Uint8Array.from(atob(encoded), char => char.charCodeAt(0)); }
    catch { throw Error('El archivo local no contiene bytes verificables.'); }
}

function base64(bytes) {
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += 32768) binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
    return btoa(binary);
}

async function withFileLock(key, task) {
    const previous = locks.get(key) || Promise.resolve();
    const pending = previous.catch(() => {}).then(() => typeof navigator !== 'undefined' && navigator.locks?.request
        ? navigator.locks.request(`offline-file:${key}`, task) : task());
    locks.set(key, pending);
    try { return await pending; } finally { if (locks.get(key) === pending) locks.delete(key); }
}

// Journal de intención antes de los bytes. Ningún original se retira por un fallo de archivo/fila.
// Se inyecta Filesystem para usar el mismo protocolo con Directory.Data nativo/web y pruebas aisladas.
export function createOfflineFileVault({ fs, directory = 'DATA', driver = getOfflineStorageDriver(), now = Date.now } = {}) {
    if (!fs?.readFile || !fs?.writeFile) throw Error('El almacenamiento de archivos no está disponible.');
    const valid = row => row?.schemaVersion === 1 && ownerId(row.userId) && ownerId(row.visitaId)
        && (row.empresaId == null || row.empresaId === ownerId(row.empresaId))
        && (row.originRevision == null || /^[a-f0-9]{64}$/.test(row.originRevision)) && /^[a-f0-9-]{36}$/.test(row.version || '')
        && /^[A-Za-z0-9_-]{1,120}$/.test(row.id || '') && row.path === `offline-files/${row.userId}/${row.id}.bin`
        && /^[a-f0-9]{64}$/.test(row.sha256 || '') && Number.isSafeInteger(row.size) && row.size > 0;
    const verify = async row => {
        if (!valid(row)) throw Error('El archivo requiere revisar identidad, tamaño o ruta.');
        const bytes = await offlineFileBytes((await fs.readFile({ path: row.path, directory })).data);
        if (bytes.length !== row.size || await hashBytes(bytes) !== row.sha256) throw Error('El archivo local no coincide con su tamaño o huella guardados.');
        return bytes;
    };
    const replace = async (previous, changes, guard = null) => {
        const next = { ...previous, ...changes, version: crypto.randomUUID(), updatedAt: now() };
        const result = await driver.mutate(STORE, previous.id, current => {
            guard?.();
            return current?.version === previous.version ? next : null;
        });
        const saved = await driver.get(STORE, previous.id);
        if (!result || JSON.stringify(saved) !== JSON.stringify(next)) throw Error('El estado del archivo cambió o no se pudo verificar.');
        return next;
    };
    const intentValid = row => row.intent?.storeName === OFFLINE_STORES.MEDIA && row.intent?.entry?.id === row.id
        && ownerId(row.intent.entry.userId) === row.userId && ownerId(row.intent.entry.visitaId) === row.visitaId
        && row.intent.entry.filePath === row.path && row.intent.entry.size === row.size;
    const publish = id => withFileLock(id, async () => {
        const row = await driver.get(STORE, id);
        if (!valid(row) || !intentValid(row) || !['ready', 'linked'].includes(row.state)) throw Error('El archivo no está listo para publicar su intención.');
        await verify(row);
        const existing = await driver.get(row.intent.storeName, id);
        if (existing) {
            if (row.state !== 'linked' || ownerId(existing.userId) !== row.userId || ownerId(existing.visitaId) !== row.visitaId || existing.filePath !== row.path || existing.size !== row.size) {
                throw Error('La intención existente del archivo requiere conciliación.');
            }
            return existing; // No pisar metadatos editados después de la publicación.
        }
        if (row.state === 'linked') throw Error('Falta una intención que ya estaba publicada; se conserva para revisión.');
        const linked = { ...row, state: 'linked', version: crypto.randomUUID(), updatedAt: now() };
        await driver.atomicPutBatch([{ storeName: STORE, value: linked }, { storeName: row.intent.storeName, value: row.intent.entry }]);
        const stored = await driver.get(STORE, id), queued = await driver.get(row.intent.storeName, id);
        if (stored?.version !== linked.version || queued?.filePath !== row.path || ownerId(queued?.userId) !== row.userId
            || ownerId(queued?.visitaId) !== row.visitaId || queued?.size !== row.size
            || JSON.stringify(encodeRowForSqlite(row.intent.storeName, queued)) !== JSON.stringify(encodeRowForSqlite(row.intent.storeName, row.intent.entry))) {
            throw Error('No se confirmó la publicación del archivo y su intención.');
        }
        return queued;
    });
    const stage = ({ id = crypto.randomUUID(), userId, visitaId, empresaId = null, originRevision = null, file, intent = null, kind = 'attachment' }) => withFileLock(id, async () => {
        const owner = ownerId(userId), visit = ownerId(visitaId);
        if (!owner || !visit || empresaId !== null && !ownerId(empresaId) || !/^[A-Za-z0-9_-]{1,120}$/.test(id)) throw Error('El archivo necesita una identidad y propietario explícitos.');
        await driver.init();
        if (driver.effectiveEngine === 'memory' || driver.constructor?.name === 'MemoryStorageDriver') throw Error('El archivo requiere almacenamiento durable.');
        const bytes = await offlineFileBytes(file);
        if (!bytes.length || file?.size !== undefined && Number(file.size) !== bytes.length) throw Error('El archivo está vacío o su tamaño no coincide.');
        const path = `offline-files/${owner}/${id}.bin`, sha256 = await hashBytes(bytes);
        if (intent && (intent.storeName !== OFFLINE_STORES.MEDIA || ownerId(intent.entry?.userId) !== owner
            || ownerId(intent.entry?.visitaId) !== visit || intent.entry?.id && intent.entry.id !== id)) throw Error('La intención del archivo tiene otra identidad o propietario.');
        const previous = await driver.get(STORE, id);
        const stamp = new Date(now()).toISOString();
        const normalizedIntent = intent ? { ...copy(intent), entry: { ...copy(intent.entry), id, userId: owner, visitaId: visit, filePath: path, size: bytes.length,
            createdAt: intent.entry.createdAt ?? previous?.intent?.entry?.createdAt ?? stamp,
            updatedAt: intent.entry.updatedAt ?? previous?.intent?.entry?.updatedAt ?? stamp } } : null;
        if (previous) {
            if (!valid(previous) || previous.userId !== owner || previous.visitaId !== visit || previous.sha256 !== sha256 || previous.size !== bytes.length
                || (previous.empresaId ?? null) !== empresaId || (previous.originRevision ?? null) !== originRevision
                || previous.kind !== kind || JSON.stringify(previous.intent) !== JSON.stringify(normalizedIntent)
                || terminal.has(previous.state) || previous.state === 'retiring') throw Error('La identidad de archivo ya pertenece a otra intención.');
            if (['ready', 'linked'].includes(previous.state)) { await verify(previous); return previous; }
        }
        const row = { id, schemaVersion: 1, version: crypto.randomUUID(), userId: owner, visitaId: visit, path, sha256, size: bytes.length,
            state: 'staged', kind, ...(empresaId !== null ? { empresaId, originRevision } : {}), createdAt: previous?.createdAt || now(), updatedAt: now(),
            intent: normalizedIntent };
        const saved = await driver.mutate(STORE, id, current => (current?.version || '') === (previous?.version || '') ? row : null);
        if (!saved || JSON.stringify(await driver.get(STORE, id)) !== JSON.stringify(row)) throw Error('No se confirmó la intención antes de guardar el archivo.');
        try { await fs.mkdir({ path: `offline-files/${owner}`, directory, recursive: true }); }
        catch (error) {
            const stat = await fs.stat?.({ path: `offline-files/${owner}`, directory });
            if (stat?.type !== 'directory') throw error;
        }
        await fs.writeFile({ path, directory, data: base64(bytes) });
        await verify(row);
        return replace(row, { state: 'ready' });
    });
    const currentSession = isCurrent => {
        if (typeof isCurrent !== 'function' || isCurrent() !== true) throw Error('La cuenta cambió. El archivo se conserva sin confirmar.');
    };
    const freezeUpload = ({ id, userId, empresaId, expectedVersion, formRevision, isCurrent }) => withFileLock(id, async () => {
        currentSession(isCurrent);
        const row = await driver.get(STORE, id);
        if (!valid(row) || row.userId !== userId || row.empresaId != null && row.empresaId !== empresaId || row.state !== 'linked' || !intentValid(row)) throw Error('El archivo no tiene una intención publicable de esta cuenta.');
        await verify(row);
        const entry = await driver.get(OFFLINE_STORES.MEDIA, id);
        const frozenManifest = row.uploadCommand ? await validateOfflineAttachmentCommand(row, row.uploadCommand) : null;
        const command = await createOfflineAttachmentCommand({ file: row, entry, empresaId, kind: row.kind,
            expectedVersion: expectedVersion === undefined ? frozenManifest?.expected_version : expectedVersion,
            formRevision: formRevision === undefined ? frozenManifest?.form_revision : formRevision });
        currentSession(isCurrent);
        if (row.uploadCommand) {
            await validateOfflineAttachmentCommand(row, row.uploadCommand);
            if (row.uploadCommand.manifest !== command.manifest || attachmentQueueSnapshot(row.uploadCommand.queueSnapshot) !== attachmentQueueSnapshot(entry)) {
                throw Error('La subida ya tiene otra intención congelada. El archivo se conserva para revisión.');
            }
            currentSession(isCurrent);
            return copy(row.uploadCommand);
        }
        const saved = await replace(row, { uploadCommand: command }, () => currentSession(isCurrent));
        if (attachmentQueueSnapshot(await driver.get(OFFLINE_STORES.MEDIA, id)) !== attachmentQueueSnapshot(entry)) {
            throw Error('La foto cambió al preparar la subida. Su intención se conserva para revisión.');
        }
        currentSession(isCurrent);
        return copy(saved.uploadCommand);
    });
    const prepareReadCopy = async row => {
        const receipt=await verifyOfflineAttachmentReceipt(row,row.uploadCommand,row.uploadReceipt);
        const manifest=await validateOfflineAttachmentCommand(row,row.uploadCommand);
        if(row.state!=='acknowledged'||manifest.kind!=='document'||manifest.mime_type!=='application/pdf')throw Error('El original no es un PDF confirmado.');
        const bytes=await verify(row),id='dep_'+row.id;
        let cached=await driver.get(STORE,id);
        if(!cached||cached.state==='staged')cached=await stage({id,userId:row.userId,empresaId:receipt.data.empresa_id,visitaId:row.visitaId,
            originRevision:row.originRevision??null,file:new Blob([bytes],{type:'application/pdf'}),kind:'downloaded_document'});
        if(cached.kind!=='downloaded_document'||cached.state!=='ready'||cached.intent!==null||cached.userId!==row.userId
            ||cached.empresaId!==receipt.data.empresa_id||cached.visitaId!==row.visitaId||cached.sha256!==row.sha256||cached.size!==row.size)
            throw Error('La copia privada del PDF requiere revisión. Se conserva el original.');
        await verify(cached);return cached;
    };
    const retireAcknowledgedQueue = async (row, isCurrent = null) => {
        if (!valid(row) || row.state !== 'acknowledged') throw Error('Falta la confirmación durable del archivo.');
        await verifyOfflineAttachmentReceipt(row, row.uploadCommand, row.uploadReceipt);
        if (isCurrent) currentSession(isCurrent);
        if(await driver.get(OFFLINE_STORES.MEDIA,row.id))
            await projectAcknowledgedAttachment(row,{driver,verifyBytes:verify,prepareReadCopy,isCurrent:()=>!isCurrent||isCurrent()===true});
        const snapshot = attachmentQueueSnapshot(row.uploadCommand.queueSnapshot);
        await driver.compareAndDelete(OFFLINE_STORES.MEDIA, row.id, entry => {
            if (isCurrent) currentSession(isCurrent);
            return attachmentQueueSnapshot(entry) === snapshot;
        });
        if (await driver.get(OFFLINE_STORES.MEDIA, row.id)) throw Error('La foto cambió después del envío. El recibo y el archivo se conservan para revisión.');
        // Tombstone y bytes conservados. La limpieza física necesita su propio protocolo de referencias.
        return row;
    };
    const acknowledgeUpload = ({ id, userId, empresaId, commandSha256, response, isCurrent }) => withFileLock(id, async () => {
        currentSession(isCurrent);
        const row = await driver.get(STORE, id);
        if (!valid(row) || row.userId !== userId || !['linked', 'acknowledged'].includes(row.state)
            || row.uploadCommand?.manifestSha256 !== commandSha256) throw Error('La confirmación no corresponde a esta intención.');
        const manifest = await validateOfflineAttachmentCommand(row, row.uploadCommand);
        if (manifest.empresa_id !== empresaId || row.empresaId != null && row.empresaId !== empresaId) throw Error('La confirmación pertenece a otra empresa.');
        const receipt = await verifyOfflineAttachmentReceipt(row, row.uploadCommand, response);
        currentSession(isCurrent);
        if (row.state === 'acknowledged') {
            await verifyOfflineAttachmentReceipt(row, row.uploadCommand, row.uploadReceipt);
            return retireAcknowledgedQueue(row, isCurrent);
        }
        const saved = await replace(row, { state: 'acknowledged', uploadReceipt: receipt }, () => currentSession(isCurrent));
        currentSession(isCurrent);
        return retireAcknowledgedQueue(saved, isCurrent);
    });
    const recover = async ({ userId = null } = {}) => {
        if (userId != null && !ownerId(userId)) throw Error('La recuperación requiere un propietario válido.');
        const rows = await driver.getAll(STORE, null, userId == null ? {} : { userId: ownerId(userId) });
        const result = { recovered: 0, needsReview: 0, retained: 0 };
        for (const observed of rows) {
            if (userId != null && observed.userId !== ownerId(userId)) continue;
            if (!valid(observed)) { result.needsReview++; continue; }
            if (terminal.has(observed.state)) {
                try {
                    if (observed.state === 'acknowledged' && observed.uploadCommand) await withFileLock(observed.id, async () => {
                        const row = await driver.get(STORE, observed.id);
                        if (row?.version !== observed.version) throw Error('El archivo cambió durante la recuperación.');
                        await retireAcknowledgedQueue(row);
                    });
                    result.retained++;
                } catch { result.needsReview++; }
                continue;
            }
            try {
                await verify(observed);
                if (observed.uploadCommand) await validateOfflineAttachmentCommand(observed, observed.uploadCommand);
                if (observed.state === 'staged') await withFileLock(observed.id, () => replace(observed, { state: 'ready' }));
                if (observed.intent) {
                    const queued = await publish(observed.id);
                    if (observed.uploadCommand && attachmentQueueSnapshot(queued) !== attachmentQueueSnapshot(observed.uploadCommand.queueSnapshot)) {
                        throw Error('La intención congelada requiere revisar cambios posteriores.');
                    }
                }
                result.recovered++;
            } catch {
                if (['downloaded_photo', 'downloaded_document'].includes(observed.kind) && observed.intent === null && !observed.uploadCommand) {
                    result.cacheNeedsReview = (result.cacheNeedsReview || 0) + 1;
                } else result.needsReview++;
            } // La caché incompleta queda pendiente; un original de usuario sigue bloqueando ante error.
        }
        return result;
    };
    return { stage, publish, verify, recover, freezeUpload, acknowledgeUpload, prepareReadCopy };
}
